import {MoneyV2} from '@shopify/hydrogen/storefront-api-types';
import {Money, useMoney} from '@shopify/hydrogen';
import clsx from 'clsx';
import {isDiscounted} from '~/lib';
import {Text} from '~/components';
import {twMerge} from 'tailwind-merge';
import {DATA_TEST_IDS} from '~/lib/const';

function CompareAtPrice({
  data,
  className,
}: {
  data: MoneyV2;
  className?: string;
}) {
  const {currencyNarrowSymbol, amount} = useMoney(data);

  let styles = twMerge(
    clsx(
      'decoration-1 text-base md:text-2xl font-normal font-header strike',
      className,
    ),
  );

  // Put color after twMerge because it is merging font size with color
  styles += ' text-gray-400';

  return (
    <span data-testid={DATA_TEST_IDS.COMPARE_AT_PRICE} className={styles}>
      {currencyNarrowSymbol}
      {amount}
    </span>
  );
}

export function ProductPriceDisplay({
  className,
  classNamePrice,
  classNameCompareAtPrice,
  showSaving,
  price,
  compareAtPrice,
  discount,
  discountedPrice,
}: {
  className?: string;
  classNamePrice?: string;
  classNameCompareAtPrice?: string;
  showSaving?: boolean;
  price?: MoneyV2;
  compareAtPrice?: MoneyV2 | null;
  discount?: number | 0;
  discountedPrice?: MoneyV2 | null;
}) {
  let baseClassNamePrice = twMerge(
    clsx('text-lg md:text-2xl font-light font-header', classNamePrice),
  );

  if (price?.amount == null) return null;

  const discounted = isDiscounted(price as MoneyV2, compareAtPrice as MoneyV2);

  if (discounted) {
    // Put color after twMerge because it is merging font size with color
    baseClassNamePrice += ' text-red-400';
  }

  const savingText =
    discounted && compareAtPrice?.amount != null && price?.amount != null
      ? Math.floor(
          ((Number(compareAtPrice.amount) - Number(price.amount)) /
            Number(compareAtPrice.amount)) *
            100,
        )
      : null;

  return (
    <div className={className}>
      <Text className="flex">
        <Money
          data-testid={DATA_TEST_IDS.PRODUCT_PRICE}
          className={baseClassNamePrice}
          withoutTrailingZeros
          data={discount && discount! > 0 ? discountedPrice! : price!}
        />
        {showSaving && savingText && savingText > 0 && discount == 0 && (
          <span
            data-testid={DATA_TEST_IDS.SAVING_PRICE}
            className={clsx('ml-5', baseClassNamePrice)}
          >
            ({savingText}% OFF)
          </span>
        )}
        {discounted && (
          <CompareAtPrice
            className={clsx('ml-5', classNameCompareAtPrice)}
            data={compareAtPrice as MoneyV2}
          />
        )}
      </Text>
    </div>
  );
}
